import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3203e62a"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "praise-modal"
}
const _hoisted_2 = { class: "praise-modal__header" }
const _hoisted_3 = { class: "praise-modal__header__title" }
const _hoisted_4 = { class: "praise-modal__actions" }

import { computed, ref, Ref } from 'vue'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { faClose } from 'fontawesome/free-solid-svg-icons'
import { faThumbsUp } from 'fontawesome/pro-regular-svg-icons'
import {
    SEND_METHOD_EMAIL,
    SEND_METHOD_SLACK,
    SEND_METHOD_TEAMS,
    SEND_METHOD_INTERNAL_NOTE,
    MOST_PROMOTER_RESPONSES,
    MOST_IMPROVED_TEAM_MEMBER,
    MOST_POSITIVE_LONG_FORM_RESPONSES,
    RECIPIENT_TYPE_LOCATION,
    ActionResponse,
    PraiseAction,
    SendMethod,
    TeamMember,
} from '@/entities/frontlineManagerDash'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Stepper from 'primevue/stepper'
import StepItem from 'primevue/stepitem'
import StepPanel from 'primevue/steppanel'
import { sendPraise } from '@/api/frontlineManagerDash'
import { validateEmail } from '@/utils/function'
import { trackAmplitude } from '@/utils/amplitude'
import {
    formatEmailList,
    processMentionsAndTags,
    doesMessageHaveMentionsOrTags,
    getSendMethodLabel,
    getPraiseGeneratorLabel,
} from '@/utils/frontlineManagerDash'
import { capitalizeFirstLetter } from '@/utils/string'
import StepCompose from '@/components/FrontlineManagerDash/PraiseModal/StepCompose.vue'
import StepDeliver from '@/components/FrontlineManagerDash/PraiseModal/StepDeliver.vue'

interface Props {
    praiseAction: PraiseAction | undefined
    installedApps: SendMethod[]
    tags: string[]
    showCarouselIndicators?: boolean
    isMobile?: boolean
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PraiseModal',
  props: /*#__PURE__*/_mergeModels({
    praiseAction: {},
    installedApps: {},
    tags: {},
    showCarouselIndicators: { type: Boolean, default: true },
    isMobile: { type: Boolean, default: false }
  }, {
    "visible": {},
    "visibleModifiers": {},
  }),
  emits: /*#__PURE__*/_mergeModels([
    'close',
    'completeAction',
    'errorDuringAction',
    'warnDuringAction',
], ["update:visible"]),
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const visible = _useModel(__props, 'visible')
const currentStep = ref(1)
const sendingPraise = ref(false)
const hasMentionsOrTags = ref(false)

const praiseTextInput = ref('')
const responseId = ref(-1)
const teamMembers: Ref<TeamMember[]> = ref([])
const selectedSendMethod = ref(SEND_METHOD_INTERNAL_NOTE)
const selectedSendMethodData = ref({})
const emails = ref('')
const emailsValidation = ref({
    hasInvalidEmail: false,
    errorMsg: '',
})

const stepPanelPt = {
    // Remove default left padding from PrimeVue StepPanel to align content properly
    content: { style: { 'margin-inline-start': '0' } },
    root: { style: { 'padding-inline-start': '0' } },
}

const recipientName = computed(() => {
    if (props.praiseAction?.userName) {
        return capitalizeFirstLetter(props.praiseAction.userName)
    }

    const isRecipientLocation =
        props.praiseAction?.recipientType === RECIPIENT_TYPE_LOCATION &&
        props.praiseAction?.location

    if (isRecipientLocation) {
        return capitalizeFirstLetter(props.praiseAction.location)
    }

    if (props.praiseAction?.recipient?.length) {
        return capitalizeFirstLetter(props.praiseAction.recipient)
    }

    return ''
})

const onSendMethodChanged = () => {
    selectedSendMethodData.value = {}
}

const headerTitle = computed(() => {
    if (currentStep.value === 1) {
        const name = recipientName.value
        return name ? `Praise your team - ${name}` : 'Praise your team'
    } else {
        return 'Where to send Praise?'
    }
})

const inputPlaceholder = computed(() => {
    switch (props.praiseAction?.generator) {
        case MOST_IMPROVED_TEAM_MEMBER:
            return 'Well done! You have the most improved score in the last week! 👏'
        case MOST_PROMOTER_RESPONSES:
            return `Great work! You’ve received ${props.praiseAction.responses.length ?? ''} promoter responses this week! 🎉`
        case MOST_POSITIVE_LONG_FORM_RESPONSES:
            return "Great job! You've received an outstanding comment from a customer! 🌟"
        default:
            return ''
    }
})

const setResponseIdToFirst = () => {
    if (props.praiseAction?.responses?.length) {
        updateResponseId(props.praiseAction.responses[0])
    }
}

const updateResponseId = (response: ActionResponse) => {
    if (response) {
        responseId.value = response.id
        teamMembers.value = response.mentions ?? []
    }
}

const goToDeliveryStep = () => {
    hasMentionsOrTags.value = doesMessageHaveMentionsOrTags(
        praiseTextInput.value,
        teamMembers.value
    )
    currentStep.value = 2
}

const wipeAndClose = () => {
    emit('close')

    // Reset values
    currentStep.value = 1
    praiseTextInput.value = ''
    selectedSendMethod.value = SEND_METHOD_INTERNAL_NOTE
    selectedSendMethodData.value = {}
    emailsValidation.value = {
        hasInvalidEmail: false,
        errorMsg: '',
    }
}

const validateEmailInput = (emailList: string[]) => {
    const hasInvalidEmails =
        !emailList.length ||
        emailList.some((email) => email && !validateEmail(email))

    emailsValidation.value = {
        hasInvalidEmail: hasInvalidEmails,
        errorMsg: hasInvalidEmails
            ? "Required field. All emails must be valid and separated with ',' or ';'"
            : '',
    }

    return emailList.length > 0 && !hasInvalidEmails
}

const submitPraise = async () => {
    let praiseMessage = praiseTextInput.value.trim() || inputPlaceholder.value

    const idToAddInternalNote =
        responseId.value !== -1
            ? responseId.value
            : props.praiseAction?.responses?.[0]?.id ?? -1

    sendingPraise.value = true

    if (selectedSendMethod.value === SEND_METHOD_EMAIL) {
        const emailList = formatEmailList(emails.value)
        const valid = validateEmailInput(emailList)

        if (!valid) {
            sendingPraise.value = false
            emit(
                'warnDuringAction',
                'Please fix the email validation issues before sending.'
            )
            return
        }

        selectedSendMethodData.value = { emails: emailList }
    }

    if (selectedSendMethod.value === SEND_METHOD_INTERNAL_NOTE) {
        praiseMessage = processMentionsAndTags(praiseMessage, teamMembers.value)
    }

    if (
        [SEND_METHOD_SLACK, SEND_METHOD_TEAMS].includes(
            selectedSendMethod.value
        )
    ) {
        if (!selectedSendMethodData.value?.name) {
            sendingPraise.value = false
            emit(
                'warnDuringAction',
                'Please select a channel for the praise to be sent to.'
            )
            return
        }
    }

    await sendPraise(
        props.praiseAction?.slug,
        praiseMessage ?? inputPlaceholder,
        Number(idToAddInternalNote),
        selectedSendMethod.value,
        selectedSendMethodData.value
    )
        .then(() => {
            wipeAndClose()
            const successToast = `Your praise ${recipientName.value ? `to ${recipientName.value}` : ''} has been sent.`
            emit('completeAction', successToast, props.praiseAction?.slug)

            const eventProperties = {
                deliveryMethod: getSendMethodLabel(selectedSendMethod.value),
                praiseType: getPraiseGeneratorLabel(
                    props.praiseAction?.generator
                ),
            }
            trackAmplitude(null, eventProperties, 'Praise Sent')
        })
        .catch(() => {
            emit(
                'errorDuringAction',
                'There was an error while sending your praise. Please try again.'
            )
        })
        .finally(() => {
            sendingPraise.value = false
        })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: visible.value,
    "onUpdate:visible": _cache[7] || (_cache[7] = ($event: any) => ((visible).value = $event)),
    modal: "",
    closable: false,
    "show-header": false,
    pt: { content: { style: { padding: 0 } } },
    "data-testid": "fm-dash-praise-modal",
    class: "praise-modal-dialog",
    onShow: setResponseIdToFirst
  }, {
    default: _withCtx(() => [
      (_ctx.praiseAction)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: _unref(faThumbsUp),
                size: "xl",
                class: "praise-modal__header__thumb-icon"
              }, null, 8, ["icon"]),
              _createElementVNode("h4", _hoisted_3, _toDisplayString(headerTitle.value), 1),
              _createElementVNode("button", {
                "aria-label": "Close",
                autofocus: "",
                class: "praise-modal__header__close",
                "data-testid": "fm-dash-close-praise-modal",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (wipeAndClose()))
              }, [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: _unref(faClose),
                  size: "xl",
                  class: "praise-modal__header__close__icon"
                }, null, 8, ["icon"])
              ])
            ]),
            _createVNode(_unref(Stepper), { value: currentStep.value }, {
              default: _withCtx(() => [
                _createVNode(_unref(StepItem), { value: 1 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(StepPanel), { pt: stepPanelPt }, {
                      default: _withCtx(() => [
                        _createVNode(StepCompose, {
                          "text-input": praiseTextInput.value,
                          "onUpdate:textInput": _cache[1] || (_cache[1] = ($event: any) => ((praiseTextInput).value = $event)),
                          responses: _ctx.praiseAction.responses,
                          "team-members": teamMembers.value,
                          "input-placeholder": inputPlaceholder.value,
                          tags: _ctx.tags,
                          "show-carousel-indicators": _ctx.showCarouselIndicators,
                          "is-mobile": _ctx.isMobile,
                          onResponseChanged: updateResponseId
                        }, null, 8, ["text-input", "responses", "team-members", "input-placeholder", "tags", "show-carousel-indicators", "is-mobile"])
                      ], undefined, true),
                      _: 1
                    })
                  ], undefined, true),
                  _: 1
                }),
                _createVNode(_unref(StepItem), { value: 2 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(StepPanel), { pt: stepPanelPt }, {
                      default: _withCtx(() => [
                        _createVNode(StepDeliver, {
                          "selected-send-method": selectedSendMethod.value,
                          "onUpdate:selectedSendMethod": _cache[2] || (_cache[2] = ($event: any) => ((selectedSendMethod).value = $event)),
                          "selected-send-method-data": 
                                selectedSendMethodData.value
                            ,
                          "onUpdate:selectedSendMethodData": _cache[3] || (_cache[3] = ($event: any) => ((
                                selectedSendMethodData.value
                            ) = $event)),
                          emails: emails.value,
                          "onUpdate:emails": _cache[4] || (_cache[4] = ($event: any) => ((emails).value = $event)),
                          "installed-apps": _ctx.installedApps,
                          "praisee-email": _ctx.praiseAction.userEmail,
                          "has-invalid-email": 
                                emailsValidation.value?.hasInvalidEmail
                            ,
                          "error-msg": emailsValidation.value?.errorMsg,
                          "has-mentions-or-tags": hasMentionsOrTags.value,
                          onSendMethodChanged: onSendMethodChanged
                        }, null, 8, ["selected-send-method", "selected-send-method-data", "emails", "installed-apps", "praisee-email", "has-invalid-email", "error-msg", "has-mentions-or-tags"])
                      ], undefined, true),
                      _: 1
                    })
                  ], undefined, true),
                  _: 1
                })
              ], undefined, true),
              _: 1
            }, 8, ["value"]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Button), {
                label: currentStep.value === 1 ? 'Cancel' : 'Back',
                class: _normalizeClass([
                        'praise-modal__actions__button',
                        'praise-modal__actions__button--cancel',
                    ]),
                severity: currentStep.value === 1 ? 'danger' : 'primary',
                outlined: "",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (
                        currentStep.value === 1 ? wipeAndClose() : (currentStep.value = 1)
                    ))
              }, null, 8, ["label", "severity"]),
              _createVNode(_unref(Button), {
                class: "praise-modal__actions__button",
                label: 
                        currentStep.value === 1
                            ? 'Next'
                            : sendingPraise.value
                              ? 'Sending...'
                              : 'Send Praise'
                    ,
                disabled: currentStep.value === 2 && sendingPraise.value,
                onClick: _cache[6] || (_cache[6] = ($event: any) => (
                        currentStep.value === 1 ? goToDeliveryStep() : submitPraise()
                    ))
              }, null, 8, ["label", "disabled"])
            ])
          ]))
        : _createCommentVNode("", true)
    ], undefined, true),
    _: 1
  }, 8, ["visible"]))
}
}

})